import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { MdKeyboardArrowUp } from "react-icons/md";
import { useIsPresent } from "framer-motion";
import { useTrackContext } from "@/state/TrackContext";
import { usePortalTarget } from "@/hooks/usePortalTarget";
import { Link, useLocation } from "react-router-dom";
import { ROUTES } from "@/consts";
import { shouldSkipExperienceSelector } from "@/consts/featureFlags";
import { TrackIcon, PlayersIcon, LocationIcon, TimeIcon, VoucherIcon } from "./icons";
import { useUrlParams } from "@/hooks/useUrlParams";
import { usePathwayStyling } from "@/hooks/usePathwayStyling";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { BOOKING_PARAM_KEYS, resolveStoreExitUrl, ROUTE_SLUGS, SCREEN_SLUGS } from "@/shared/utils/bookingUrl";
import { getLayoutWidth } from "@/shared/utils/layoutViewport";

interface ProgressBarProps {
  maxValue?: number; // Optional: Progress is now calculated internally based on current step
  currentValue?: number; // Optional: Progress is now calculated internally based on current step
}

interface IBreadcrumbs {
  title: string;
  link: string | null;
  icon?: React.ReactNode;
  isActive?: boolean;
  /** Show the icon in place of the label on mobile, even while this step is the active one. */
  iconOnlyOnMobile?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ maxValue, currentValue }) => {
  const [progress, setProgress] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState<number>(getLayoutWidth);
  const prefersReducedMotion = usePrefersReducedMotion();
  const location = useLocation();
  const {
    trackSelectionOption: { title },
    activeKids,
    dispatch,
    progressiveBarBreadcrumbsRoutes,
    appHostedAt,
    metaObjectTrackSelectionConfig,
    visitorLocation: { suburb, state, postcode },
    hasProvidedPostcode,
    navigationPreviousInfo,
    isAnyItemsAvailableInCart,
    showArcadeBanner,
  } = useTrackContext();

  // Get selectedRoute and selectedScreen from URL params
  const { getUrlParams, navigateWithParams } = useUrlParams();
  const { selectedRoute, selectedScreen } = getUrlParams();
  const { pathwayStyle, isBattleKart } = usePathwayStyling();

  const progressLabelFontSize = pathwayStyle.progressBar?.labelFontSize ?? "14px";
  const progressLabelColor = pathwayStyle.progressBar?.labelColor ?? "#ffffff";

  const effectiveTitle = useMemo(() => {
    const routeToTrackMap: Record<string, string> = {
      [ROUTE_SLUGS.MAIN]: "MAIN_TRACK",
      [ROUTE_SLUGS.INTERMEDIATE]: "INTERMEDIATE_TRACK",
      [ROUTE_SLUGS.MINI]: "MINI_TRACK",
      [ROUTE_SLUGS.JUNIOR]: "JUNIOR_TRACK",
      [ROUTE_SLUGS.BATTLE_KART]: "BATTLE_KART_TRACK",
      [ROUTE_SLUGS.TRACKS]: "RACING_GAMES_TRACK",
      [ROUTE_SLUGS.ARCADE]: "ARCADE",
    };
    if (selectedRoute && routeToTrackMap[selectedRoute]) {
      return routeToTrackMap[selectedRoute];
    }
    return title;
  }, [selectedRoute, title]);

  const isVisitorLocationInfoAvailable = useMemo(
    () => hasProvidedPostcode || (suburb && suburb !== "" && state && state !== "" && postcode && postcode !== ""),
    [suburb, state, postcode, hasProvidedPostcode],
  );

  // Step detection - determine current step from the screen segment of the URL.
  const getCurrentActiveStep = useCallback(() => {
    const screen = selectedScreen || location.pathname.split("/").filter(Boolean)[1] || "";

    switch (screen) {
      case SCREEN_SLUGS.EXPERIENCE:
      case SCREEN_SLUGS.CART:
      case SCREEN_SLUGS.EXTRAS:
      case SCREEN_SLUGS.SUMMARY:
        return 99;
      case SCREEN_SLUGS.DATE:
        return 3;
      case SCREEN_SLUGS.VOUCHER:
      case SCREEN_SLUGS.LOCATION:
        return 2;
      case SCREEN_SLUGS.SEATS:
      case SCREEN_SLUGS.PLAYERS:
      case SCREEN_SLUGS.CARD:
        return 1;
      default:
        return 0;
    }
  }, [location.pathname, selectedScreen]);

  const currentActiveStep = getCurrentActiveStep();

  // Must match the condition that inserts the Voucher crumb below, or the fill and the
  // labels disagree about how many steps there are.
  const hasVoucherStep =
    activeKids.optedIn && effectiveTitle !== "RACING_GAMES_TRACK" && effectiveTitle !== "ARCADE";

  // Calculate total steps for the current track (excluding empty breadcrumbs)
  const getTotalSteps = useCallback(() => {
    if (selectedRoute === ROUTE_SLUGS.TRACKS) {
      return 3; // Racing, Drivers, Times
    } else if (effectiveTitle === "RACING_GAMES_TRACK") {
      return 1; // Choose Your Experience
    } else if (effectiveTitle === "ARCADE") {
      return 2; // Arcade, Select Card
    } else if (isVisitorLocationInfoAvailable) {
      // If location is available, skip the postcode step
      return 3; // Track, Players/Drivers, Time
    } else {
      // If location is not available, include postcode step
      return 4; // Track, Players/Drivers, Postcode, Time
    }
  }, [selectedRoute, effectiveTitle, isVisitorLocationInfoAvailable]);

  /**
   * Where we are in the sequence the customer can actually see.
   *
   * Kept apart from `currentActiveStep`, which the breadcrumb links compare against and
   * whose numbering predates the voucher step. The bar counts the voucher screen as a
   * step of its own, so everything after it shifts along by one.
   */
  const visibleProgress = useCallback(() => {
    const screen = selectedScreen || location.pathname.split("/").filter(Boolean)[1] || "";
    const extra = hasVoucherStep ? 1 : 0;

    if (screen === SCREEN_SLUGS.VOUCHER) return { step: 2, totalSteps: getTotalSteps() + extra };
    if (screen === SCREEN_SLUGS.LOCATION) {
      return { step: 2 + extra, totalSteps: getTotalSteps() + extra };
    }
    if (screen === SCREEN_SLUGS.DATE) {
      return { step: 3 + extra, totalSteps: getTotalSteps() + extra };
    }
    return { step: currentActiveStep, totalSteps: getTotalSteps() + extra };
  }, [currentActiveStep, getTotalSteps, hasVoucherStep, location.pathname, selectedScreen]);

  // Calculate progress based on current step and total steps
  const calculateProgress = useCallback(() => {
    // Special case: Review/Cart page should always show 100%
    if (currentActiveStep === 99) {
      return 100;
    }

    if (effectiveTitle === "RACING_GAMES_TRACK" && currentActiveStep === 0) {
      return 50;
    }

    const { step, totalSteps } = visibleProgress();
    const stepPercentage = 100 / totalSteps;

    // Add extra progress within the current step for better UX
    // Each step fills its segment gradually
    let currentStepProgress = step * stepPercentage;

    // Add partial progress within the current step
    // For the last step before review (time selection), cap at 95%
    // For other steps, add up to 80% of the step
    if (step === totalSteps - 1) {
      // Time selection step - cap at around 95%
      const partialProgress = stepPercentage * 0.6; // Less progress within this step
      currentStepProgress += partialProgress;
      return Math.min(currentStepProgress, 95); // Cap at 95% max
    } else {
      const partialProgress = stepPercentage * 0.8;
      currentStepProgress += partialProgress;
    }

    // Cap at 95% for time selection, 100% otherwise
    return Math.min(currentStepProgress, 95);
  }, [currentActiveStep, effectiveTitle, visibleProgress]);

  useEffect(() => {
    // Calculate progress based on current step instead of props
    const calculatedProgress = calculateProgress();
    setProgress(calculatedProgress);

    const uniqueItems = new Set([...progressiveBarBreadcrumbsRoutes, location.pathname]);
    if (!progressiveBarBreadcrumbsRoutes.includes(location.pathname)) {
      dispatch({
        type: "SET_PROGRESSIVE_BAR_BREADCRUMBS_ROUTE",
        payload: {
          progressiveBarBreadcrumbsRoutes: [...uniqueItems],
        },
      });
    }
  }, [calculateProgress, progressiveBarBreadcrumbsRoutes, location.pathname, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(getLayoutWidth());
    };
    window.addEventListener("resize", handleResize);

    dispatch({
      type: "SET_WINDOW_WIDTH",
      payload: {
        windowWidth: windowWidth,
      },
    });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch, windowWidth]);

  const getIconForBreadcrumb = useCallback((breadcrumbTitle: string, trackType: string) => {
    const iconSize = 24;

    if (trackType === "BATTLE_KART_TRACK") {
      if (breadcrumbTitle.toLowerCase().includes("battlekart")) {
        // Use ticket icon (TrackIcon) instead of BattleKartIcon for the first step
        return <TrackIcon width={iconSize} height={iconSize} />;
      }
    } else if (breadcrumbTitle.toLowerCase().includes("track")) {
      return <TrackIcon width={iconSize} height={iconSize} />;
    }

    if (breadcrumbTitle.toLowerCase().includes("voucher")) {
      return <VoucherIcon width={iconSize} height={iconSize} />;
    }

    if (breadcrumbTitle.toLowerCase().includes("driver") || breadcrumbTitle.toLowerCase().includes("player")) {
      return <PlayersIcon width={iconSize} height={iconSize} />;
    } else if (breadcrumbTitle.toLowerCase().includes("postcode") || breadcrumbTitle.toLowerCase().includes("location")) {
      return <LocationIcon width={iconSize} height={iconSize} />;
    } else if (breadcrumbTitle.toLowerCase().includes("time") || breadcrumbTitle.toLowerCase().includes("date")) {
      return <TimeIcon />;
    }

    return <TrackIcon width={iconSize} height={iconSize} />;
  }, []);

  let breadcrumbs: IBreadcrumbs[] =
    selectedRoute === ROUTE_SLUGS.TRACKS
      ? [
          {
            title: "Racing",
            link: ROUTES.INDEX,
            icon: getIconForBreadcrumb("Racing", effectiveTitle),
            isActive: currentActiveStep === 0,
          },
          {
            title: "Drivers",
            link:
              currentActiveStep > 1 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES : null,
            icon: getIconForBreadcrumb("Drivers", effectiveTitle),
            isActive: currentActiveStep === 1,
          },
          {
            title: "Times",
            link:
              currentActiveStep >= 3 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME : null,
            icon: getIconForBreadcrumb("Times", effectiveTitle),
            isActive: currentActiveStep === 3,
          },
        ]
      : effectiveTitle === "MAIN_TRACK" && windowWidth <= 768
        ? [
            {
              title:
                metaObjectTrackSelectionConfig && metaObjectTrackSelectionConfig.mainTrack && metaObjectTrackSelectionConfig.mainTrack.title
                  ? metaObjectTrackSelectionConfig.mainTrack.title
                  : "main Track",
              link: "/" + ROUTES.RACING_GAMES_TRACK.INDEX,
              icon: getIconForBreadcrumb("main Track", effectiveTitle),
              isActive: currentActiveStep === 0,
            },
            {
              title: "Drivers",
              link:
                currentActiveStep > 1 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES)
                  ? ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES
                  : null,
              icon: getIconForBreadcrumb("Drivers", effectiveTitle),
              isActive: currentActiveStep === 1,
            },
            {
              title: "Time",
              link:
                currentActiveStep >= 3 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME : null,
              icon: getIconForBreadcrumb("Time", effectiveTitle),
              isActive: currentActiveStep === 3,
            },
          ]
        : effectiveTitle === "MAIN_TRACK"
          ? [
              {
                title:
                  metaObjectTrackSelectionConfig && metaObjectTrackSelectionConfig.mainTrack && metaObjectTrackSelectionConfig.mainTrack.title
                    ? metaObjectTrackSelectionConfig.mainTrack.title
                    : "main Track",
                link: "/" + ROUTES.RACING_GAMES_TRACK.INDEX,
                icon: getIconForBreadcrumb("main Track", effectiveTitle),
                isActive: currentActiveStep === 0,
              },
              {
                title: "Drivers",
                link:
                  currentActiveStep > 1 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES)
                    ? ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES
                    : null,
                icon: getIconForBreadcrumb("Drivers", effectiveTitle),
                isActive: currentActiveStep === 1,
              },
              {
                title: "Time",
                link:
                  currentActiveStep >= 3 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME)
                    ? ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME
                    : null,
                icon: getIconForBreadcrumb("Time", effectiveTitle),
                isActive: currentActiveStep === 3,
              },
            ]
          : effectiveTitle === "INTERMEDIATE_TRACK" && windowWidth <= 768
            ? [
                {
                  title: "Intermediate Track",
                  link: "/" + ROUTES.RACING_GAMES_TRACK.INDEX,
                  icon: getIconForBreadcrumb("Intermediate Track", effectiveTitle),
                  isActive: currentActiveStep === 0,
                },
                {
                  title: "Drivers",
                  link:
                    currentActiveStep > 1 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.SEATS_RACES)
                      ? ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.SEATS_RACES
                      : null,
                  icon: getIconForBreadcrumb("Drivers", effectiveTitle),
                  isActive: currentActiveStep === 1,
                },
                {
                  title: "Time",
                  link:
                    currentActiveStep >= 3 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.DATE_TIME)
                      ? ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.DATE_TIME
                      : null,
                  icon: getIconForBreadcrumb("Time", effectiveTitle),
                  isActive: currentActiveStep === 3,
                },
              ]
            : effectiveTitle === "INTERMEDIATE_TRACK"
              ? [
                  {
                    title: "Intermediate Track",
                    link: "/" + ROUTES.RACING_GAMES_TRACK.INDEX,
                    icon: getIconForBreadcrumb("Intermediate Track", effectiveTitle),
                    isActive: currentActiveStep === 0,
                  },
                  {
                    title: "Drivers",
                    link:
                      currentActiveStep > 1 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.SEATS_RACES)
                        ? ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.SEATS_RACES
                        : null,
                    icon: getIconForBreadcrumb("Drivers", effectiveTitle),
                    isActive: currentActiveStep === 1,
                  },
                  {
                    title: "Time",
                    link:
                      currentActiveStep >= 3 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.DATE_TIME)
                        ? ROUTES.INDEX + ROUTES.INTERMEDIATE_TRACK.DATE_TIME
                        : null,
                    icon: getIconForBreadcrumb("Time", effectiveTitle),
                    isActive: currentActiveStep === 3,
                  },
                ]
              : effectiveTitle === "MINI_TRACK"
                ? [
                    {
                      title: "mini track",
                      link: "/" + ROUTES.RACING_GAMES_TRACK.INDEX,
                      icon: getIconForBreadcrumb("mini track", effectiveTitle),
                      isActive: currentActiveStep === 0,
                    },
                    {
                      title: "Drivers",
                      link:
                        currentActiveStep > 1 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MINI_TRACK.SEATS_RACES)
                          ? ROUTES.INDEX + ROUTES.MINI_TRACK.SEATS_RACES
                          : null,
                      icon: getIconForBreadcrumb("Drivers", effectiveTitle),
                      isActive: currentActiveStep === 1,
                    },
                    {
                      title: "Time",
                      link:
                        currentActiveStep >= 3 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MINI_TRACK.DATE_TIME)
                          ? ROUTES.INDEX + ROUTES.MINI_TRACK.DATE_TIME
                          : null,
                      icon: getIconForBreadcrumb("Time", effectiveTitle),
                      isActive: currentActiveStep === 3,
                    },
                  ]
                : effectiveTitle === "JUNIOR_TRACK"
                  ? [
                      {
                        title: "junior track",
                        link: "/" + ROUTES.RACING_GAMES_TRACK.INDEX,
                        icon: getIconForBreadcrumb("junior track", effectiveTitle),
                        isActive: currentActiveStep === 0,
                      },
                      {
                        title: "Drivers",
                        link:
                          currentActiveStep > 1 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.JUNIOR_TRACK.SEATS_RACES)
                            ? ROUTES.INDEX + ROUTES.JUNIOR_TRACK.SEATS_RACES
                            : null,
                        icon: getIconForBreadcrumb("Drivers", effectiveTitle),
                        isActive: currentActiveStep === 1,
                      },
                      {
                        title: "Time",
                        link:
                          currentActiveStep >= 3 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.JUNIOR_TRACK.DATE_TIME)
                            ? ROUTES.INDEX + ROUTES.JUNIOR_TRACK.DATE_TIME
                            : null,
                        icon: getIconForBreadcrumb("Time", effectiveTitle),
                        isActive: currentActiveStep === 3,
                      },
                    ]
                  : effectiveTitle === "RACING_GAMES_TRACK"
                    ? [
                        {
                          title: "Experiences",
                          link: null,
                          icon: getIconForBreadcrumb("Experiences", effectiveTitle),
                          isActive: currentActiveStep === 0,
                        },
                        {
                          title: "Preview",
                          link: null,
                          icon: getIconForBreadcrumb("Preview", effectiveTitle),
                          isActive: currentActiveStep === 1,
                        },
                      ]
                    : effectiveTitle === "BATTLE_KART_TRACK"
                      ? [
                          {
                            title: "battleKart",
                            link: "/",
                            icon: getIconForBreadcrumb("battleKart", effectiveTitle),
                            isActive: currentActiveStep === 0,
                          },
                          {
                            title: "Players",
                            link: ROUTES.INDEX + ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS,
                            icon: getIconForBreadcrumb("Players", effectiveTitle),
                            isActive: currentActiveStep === 1,
                          },
                          {
                            title: "Time",
                            link: ROUTES.INDEX + ROUTES.BATTLE_KART_TRACK.DATE_TIME,
                            icon: getIconForBreadcrumb("Time", effectiveTitle),
                            isActive: currentActiveStep === 3,
                          },
                        ]
                      : effectiveTitle === "ARCADE"
                        ? [
                            {
                              title: "Arcade",
                              link: "/",
                              icon: getIconForBreadcrumb("Arcade", effectiveTitle),
                              isActive: currentActiveStep === 0,
                            },
                            {
                              title: "Select Card",
                              link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.ARCADE.ARCADE_CARD) ? ROUTES.INDEX + ROUTES.ARCADE.ARCADE_CARD : null,
                              icon: getIconForBreadcrumb("Select Card", effectiveTitle),
                              isActive: currentActiveStep === 1,
                            },
                          ]
                        : [];

  if (!isVisitorLocationInfoAvailable && effectiveTitle !== "RACING_GAMES_TRACK" && effectiveTitle !== "ARCADE" && selectedRoute !== ROUTE_SLUGS.TRACKS) {
    const postCodeRouteMap: Record<string, string> = {
      MAIN_TRACK: ROUTES.MAIN_TRACK.POST_CODE,
      INTERMEDIATE_TRACK: ROUTES.INTERMEDIATE_TRACK.POST_CODE,
      MINI_TRACK: ROUTES.MINI_TRACK.POST_CODE,
      JUNIOR_TRACK: ROUTES.JUNIOR_TRACK.POST_CODE,
      BATTLE_KART_TRACK: ROUTES.BATTLE_KART_TRACK.POST_CODE,
    };

    const postCodeRoute = postCodeRouteMap[effectiveTitle];

    if (postCodeRoute) {
      const postcodeBreadcrumb: IBreadcrumbs = {
        title: windowWidth <= 768 ? "Location" : "Postcode",
        link: currentActiveStep >= 2 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + postCodeRoute) ? ROUTES.INDEX + postCodeRoute : null,
        icon: getIconForBreadcrumb("Postcode", effectiveTitle),
        isActive: selectedScreen === SCREEN_SLUGS.LOCATION,
      };

      breadcrumbs.splice(breadcrumbs.length - 1, 0, postcodeBreadcrumb);
    }
  }

  // The voucher step sits between seats-and-races and postcode, and only exists once the
  // customer has opted in - same conditional shape as the postcode crumb above.
  if (activeKids.optedIn && effectiveTitle !== "RACING_GAMES_TRACK" && effectiveTitle !== "ARCADE") {
    const activeKidsRouteMap: Record<string, string> = {
      MAIN_TRACK: ROUTES.MAIN_TRACK.ACTIVE_KIDS,
      INTERMEDIATE_TRACK: ROUTES.INTERMEDIATE_TRACK.ACTIVE_KIDS,
      MINI_TRACK: ROUTES.MINI_TRACK.ACTIVE_KIDS,
      JUNIOR_TRACK: ROUTES.JUNIOR_TRACK.ACTIVE_KIDS,
      BATTLE_KART_TRACK: ROUTES.BATTLE_KART_TRACK.ACTIVE_KIDS,
    };

    const activeKidsRoute = activeKidsRouteMap[effectiveTitle];

    if (activeKidsRoute) {
      const insertBefore = breadcrumbs.findIndex((crumb) => crumb.title === "Postcode" || crumb.title === "Location");

      breadcrumbs.splice(insertBefore >= 0 ? insertBefore : breadcrumbs.length - 1, 0, {
        title: "Voucher",
        link:
          currentActiveStep >= 2 || progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + activeKidsRoute)
            ? ROUTES.INDEX + activeKidsRoute
            : null,
        icon: getIconForBreadcrumb("Voucher", effectiveTitle),
        isActive: selectedScreen === SCREEN_SLUGS.VOUCHER,
        // "Voucher" is the longest label in the bar and crowds the others out on a phone.
        iconOnlyOnMobile: true,
      });
    }
  }

  const isMobileLarge = windowWidth >= 425 && windowWidth <= 768;

  const renderBreadcrumbs = () => {
    const handleBreadcrumbClick = (link: string) => {
      const paramsHomepage = new URLSearchParams(window.location.search);
      const homepageKey = BOOKING_PARAM_KEYS.homepage;
      if (paramsHomepage.get(homepageKey) === "0") {
        paramsHomepage.set(homepageKey, "1");
        const newUrl = `${window.location.pathname}?${paramsHomepage.toString()}`;
        window.history.pushState({}, "", newUrl);
      }
      return link;
    };

    const isMobile = windowWidth <= 768;

    // Filter breadcrumbs on mobile: when on step 0 for RACING_GAMES_TRACK, only show current step
    const filteredBreadcrumbs =
      isMobile && effectiveTitle === "RACING_GAMES_TRACK" && currentActiveStep === 0 ? breadcrumbs.filter((breadcrumb) => breadcrumb.isActive) : breadcrumbs;

    return (
      <>
        {filteredBreadcrumbs.map((breadcrumb: IBreadcrumbs, index: number) => {
          const iconInsteadOfLabel = isMobile && !!breadcrumb.icon && breadcrumb.iconOnlyOnMobile;
          const showText = !isMobile || (breadcrumb.isActive && !iconInsteadOfLabel);
          const showIcon = isMobile && !!breadcrumb.icon && (!breadcrumb.isActive || iconInsteadOfLabel);

          const content = (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1">
              {showIcon && <div className="tw-flex tw-items-center tw-justify-center">{breadcrumb.icon}</div>}
              {showText && (
                <span className="tw-whitespace-nowrap" style={{ fontSize: progressLabelFontSize, color: progressLabelColor }}>
                  {breadcrumb.title}
                </span>
              )}
            </div>
          );

          return breadcrumb.link && breadcrumb.title !== "" ? (
            <Link
              to={handleBreadcrumbClick(breadcrumb.link)}
              key={index}
              className={`tw-text-white hover:tw-text-[#000] hover:tw-opacity-[60%] ${
                effectiveTitle === "MAIN_TRACK" && windowWidth <= 600 && breadcrumb.title === "date & time" ? "tw-mr-[-24px]" : ""
              } tw-w-full tw-font-[700] tw-capitalize tw-text-center tw-max-h-[70px] md:tw-max-h-[30px] tw-h-full tw-flex tw-justify-center tw-items-center ${
                effectiveTitle === "BATTLE_KART_TRACK" ? "add-montreal-heavy-font" : ""
              }`}
              style={{
                wordBreak: "break-word",
                textDecoration: "none",
              }}
            >
              {content}
            </Link>
          ) : (
            breadcrumb.title !== "" && (
              <span
                key={index}
                className={`tw-text-white ${
                  effectiveTitle === "MAIN_TRACK" && windowWidth <= 600 && breadcrumb.title === "date & time" ? "tw-mr-[-24px]" : ""
                } tw-w-full tw-font-[700] tw-capitalize tw-text-center tw-max-h-[70px] md:tw-max-h-[30px] tw-h-full tw-flex tw-justify-center tw-items-center tw-cursor-not-allowed ${
                  effectiveTitle === "BATTLE_KART_TRACK" ? "add-montreal-heavy-font" : ""
                }`}
                style={{
                  wordBreak: "break-word",
                }}
              >
                {content}
              </span>
            )
          );
        })}
      </>
    );
  };

  const portalTarget = usePortalTarget("progress-bar-portal");
  const isPresent = useIsPresent();

  const isOnHomePage = !selectedRoute;

  // The racing age step is the first screen while the experience selector is
  // skipped (see featureFlags.ts), so nothing in the app precedes it.
  const isFirstStepRacingAgePage =
    selectedRoute === ROUTE_SLUGS.TRACKS && !selectedScreen && shouldSkipExperienceSelector({ isAnyItemsAvailableInCart, showArcadeBanner, appHostedAt });

  const handlePreviousClick = useCallback(() => {
    // Checked before navigationPreviousInfo, which screens set but never clear:
    // on the first step the stale value would point further into the funnel.
    if (isFirstStepRacingAgePage) {
      window.location.assign(resolveStoreExitUrl({ referrer: document.referrer, appOrigin: window.location.origin }));
      return;
    }
    if (navigationPreviousInfo) {
      navigateWithParams(navigationPreviousInfo.pagePath);
      return;
    }
    navigateWithParams("/");
  }, [isFirstStepRacingAgePage, navigationPreviousInfo, navigateWithParams]);

  if (!isPresent) return null;

  const progressBackground =
    pathwayStyle.progressBar?.background ??
    (isBattleKart
      ? "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)"
      : "linear-gradient(270deg, #F908FF 0%, #D32EFE 8.19%, #37CCFB 87.88%, #05FFFA 100%)");
  // Full pill by default, which is the shape the mobile header ships with.
  const progressBorderRadius = pathwayStyle.progressBar?.borderRadius ?? "9999px";

  const previousBtnBackground = pathwayStyle.previousButton?.background ?? "#4A4C52";
  const previousBtnIconColor = pathwayStyle.previousButton?.iconColor ?? "#ffffff";
  const previousBtnBorderRadius = pathwayStyle.previousButton?.borderRadius ?? "9999px";
  const previousBtnBorderColor = pathwayStyle.previousButton?.borderColor ?? "rgba(255, 255, 255, 0.7)";

  // Point the right edge of the fill into an arrow. Using a fixed px inset (via calc)
  // keeps the arrow depth constant regardless of the fill width, and since the fill
  // carries the gradient there is no separate tip color to maintain.
  const progressArrowClipPath = "polygon(0% 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 0% 100%)";

  const isMobile = windowWidth <= 768;
  const showBackButton = isMobile && !isOnHomePage;

  const barHeightClass = `${isMobile ? "tw-h-[35px]" : isMobileLarge ? "tw-h-[40px]" : "tw-h-[30px]"} md:tw-h-[30px]`;

  // Mirrors the date & time page: on mobile the header is a row of pills - a Back
  // circle shaped like the filter button, inset from the left, beside the progress
  // oval, which runs to the screen edge. Desktop keeps the original edge-to-edge
  // square bar, so the theme's radius applies to the mobile pill only.
  const progressTrack = (
    <div
      className={`tw-flex tw-flex-1 tw-min-w-0 tw-items-center ${barHeightClass} tw-bg-[#393B41] tw-overflow-hidden tw-relative md:tw-rounded-none`}
      style={{ borderRadius: isMobile ? progressBorderRadius : undefined }}
    >
      <div className={`tw-absolute tw-top-[0] tw-left-[0] tw-z-10 tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center ${isMobile ? "tw-px-2" : ""}`}>
        {renderBreadcrumbs()}
      </div>
      <div
        style={{
          width: `${progress}%`,
          height: "100%",
          transition: prefersReducedMotion ? undefined : "width 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
          background: progressBackground,
          clipPath: progressArrowClipPath,
        }}
        className={`${barHeightClass} tw-relative`}
      />
    </div>
  );

  const content =
    !appHostedAt && appHostedAt !== "bosshunting" ? (
      <div className={`tw-w-full tw-flex tw-items-center ${isMobile ? "tw-gap-2 tw-pl-[10px]" : ""}`}>
        {showBackButton && (
          <button
            className={`tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-aspect-square ${barHeightClass}`}
            onClick={handlePreviousClick}
            aria-label="Back"
            style={{ background: previousBtnBackground, borderRadius: previousBtnBorderRadius, boxShadow: `inset 0 0 0 1.5px ${previousBtnBorderColor}` }}
          >
            <MdKeyboardArrowUp className="tw-text-[28px] tw-shrink-0" style={{ color: previousBtnIconColor }} />
          </button>
        )}
        {progressTrack}
      </div>
    ) : null;

  return portalTarget && content ? createPortal(content, portalTarget) : content;
};

export default ProgressBar;
